import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { TREE_NODE } from '../constants/urlParams';
import * as atoms from '../atoms/atoms';
import useSearchParams from './useSearchParams';
import useSelectedTreeItem from './useSelectedTreeItem';

type Props = {
  pageTitle: string;
};

/**
 * Parses a folder node id to extract the folder path segments.
 * e.g., "folderNode_staging/folderNode_models" -> ["staging", "models"]
 */
const parseFolderPath = (folderId: string): string[] =>
  folderId
    .split('/')
    .map((segment) => segment.replace('folderNode_', ''))
    .filter(Boolean);

export const useBreadcrumbs = ({ pageTitle }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTreeNodesById = useRecoilValue(atoms.currentTreeNodesById);

  const { selectedTreeNode } = useSelectedTreeItem();

  return useMemo(() => {
    let path: string[] = [];

    if (selectedTreeNode) {
      const fullNode = currentTreeNodesById[selectedTreeNode.id];

      if (selectedTreeNode.resourceType === 'folder') {
        // For folders, parse the id to get the path
        path = parseFolderPath(selectedTreeNode.id);
      } else if (fullNode) {
        // For leaf nodes (models, sources, etc.), derive path from parentId + name
        const parentPath = fullNode.parentId
          ? parseFolderPath(fullNode.parentId)
          : [];
        path = [...parentPath, fullNode.name];
      }
    }

    return [
      {
        label: pageTitle,
        onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          e.preventDefault();
          const newParams = new URLSearchParams(searchParams);
          newParams.delete(TREE_NODE);
          setSearchParams(newParams, { replace: false });
        },
      },
      ...path.map((i, ind, arr) => ({
        label: i,
        onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          e.preventDefault();
          const newParams = new URLSearchParams(searchParams);
          const id = arr
            .slice(0, ind + 1)
            .map((name) => `folderNode_${name}`)
            .join('/');
          newParams.set(TREE_NODE, JSON.stringify({ id }));
          setSearchParams(newParams, { replace: false });
        },
      })),
    ];
  }, [
    pageTitle,
    selectedTreeNode,
    currentTreeNodesById,
    searchParams,
    setSearchParams,
  ]);
};
